import { ZfFormField, ZfInput, ZfSelectOption, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import moment from 'moment/moment';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ControllerRenderProps, Validate } from 'react-hook-form';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateSurplusItem, useUpdateSurplusItem } from '../../../api';
import type { PanelSettingsDto, SurplusItemSettingsDto, SurplusType } from '../../../api/generated/client';
import { getDateFromIsoDateString, getIsoDateFromDateString, getMomentFromStringOrElseNull, isValidSurplusDateRange } from '../../../utils/date-utils';
import { requiredField } from '../../../utils/form-utils';

type SurplusFormEditMode = 'CREATE' | 'UPDATE';

type SurplusFormValues = {
    surplusType?: SurplusType;
    amount?: string;
    fromDate?: string;
    toDate?: string;
};

interface IProps {
    cashflowOverrideSettings: PanelSettingsDto;
    surplusItem?: SurplusItemSettingsDto;
    cancel: () => void;
    deleteItem: (surplusItemId: number | undefined) => void;
}

export const SurplusForm: FunctionComponent<IProps> = ({ cashflowOverrideSettings, surplusItem, cancel, deleteItem }) => {
    const { t } = useTranslation();
    const [editMode, setEditMode] = useState<SurplusFormEditMode>('CREATE');
    const createSurplusItem = useCreateSurplusItem();
    const updateSurplusItem = useUpdateSurplusItem();

    const { control, handleSubmit, reset, trigger } = useForm<SurplusFormValues>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            surplusType: surplusItem?.surplusType,
            amount: surplusItem?.amount ? `${surplusItem?.amount}` : '0',
            fromDate: surplusItem?.fromDate,
            toDate: surplusItem?.toDate,
        },
    });
    const { isDirty, isValid, dirtyFields, errors } = useFormState<SurplusFormValues>({ control });
    const [watchedFromDate, watchedToDate] = useWatch({ control, name: ['fromDate', 'toDate'] });

    const isValidDateRange = useMemo(() => {
        const existingDateRanges = !cashflowOverrideSettings?.itemsSettings?.length
            ? []
            : cashflowOverrideSettings.itemsSettings.map((item) => ({
                  from: getMomentFromStringOrElseNull(item.fromDate),
                  id: item.id,
                  to: getMomentFromStringOrElseNull(item.toDate),
              }));

        return isValidSurplusDateRange(existingDateRanges, watchedFromDate, watchedToDate, surplusItem?.id);
    }, [cashflowOverrideSettings.itemsSettings, surplusItem?.id, watchedFromDate, watchedToDate]);

    const resetForm = useCallback(
        (applySurplusItem?: boolean) => {
            reset({
                surplusType: applySurplusItem ? surplusItem?.surplusType : undefined,
                amount: applySurplusItem ? (surplusItem?.amount ? `${surplusItem?.amount}` : '0') : undefined,
                fromDate: applySurplusItem ? getIsoDateFromDateString(surplusItem?.fromDate) : undefined,
                toDate: applySurplusItem ? getIsoDateFromDateString(surplusItem?.toDate) : undefined,
            });
        },
        [reset, surplusItem],
    );

    const getAmountNumber = (amount?: string) => (amount ? parseFloat(amount.replace(/,/g, '')) : 0);

    useEffect(() => {
        if (surplusItem) {
            resetForm(true);
            setEditMode('UPDATE');
        } else {
            resetForm();
            setEditMode('CREATE');
        }
    }, [surplusItem, resetForm]);

    const onSubmit = handleSubmit(({ surplusType, amount, fromDate, toDate }) => {
        if (!isValid) return;

        if (editMode === 'CREATE') {
            createSurplusItem.mutate({
                clientId: -1,
                enabled: true,
                surplusType: surplusType!,
                amount: getAmountNumber(amount),
                fromDate: getDateFromIsoDateString(fromDate),
                toDate: getDateFromIsoDateString(toDate),
            });
            resetForm();
        } else {
            updateSurplusItem.mutate({
                ...(surplusItem ?? { clientId: -1, enabled: true }),
                surplusType: surplusType!,
                amount: getAmountNumber(amount),
                fromDate: getDateFromIsoDateString(fromDate),
                toDate: getDateFromIsoDateString(toDate),
            });
            cancelUpdate();
        }
    });

    const isSubmitDisabled = () => {
        if ('CREATE' === editMode) {
            if (dirtyFields.surplusType && dirtyFields.amount && dirtyFields.fromDate && dirtyFields.toDate) {
                return !isValid || !isValidDateRange;
            }
        }
        if (isDirty) return !isValid || !isValidDateRange;

        return true;
    };

    const cancelUpdate = () => {
        cancel();
        setEditMode('CREATE');
    };

    const onDateFieldChange = (newValue: string, onFieldChange: ControllerRenderProps['onChange']) => {
        onFieldChange(newValue);
        trigger(['fromDate', 'toDate']);
    };

    const validateAmount: Validate<string | undefined, SurplusFormValues> = (value) => (value && getAmountNumber(value) > 0) || t('amountGreaterThan0');
    const validateToDate: Validate<string | undefined, SurplusFormValues> = (value, formValues) => {
        const fromDate = moment(formValues.fromDate);
        const toDate = moment(value);

        return !fromDate || (!!toDate && toDate.isAfter(fromDate)) || t('toDateMustBeAfterFromDate');
    };

    return (
        <ZfStack direction="column" spacing="extra-large">
            <ZfTypography variant="body-medium" color="emphasis" style={{ marginTop: 'var(--padding-top-xs)' }}>
                {surplusItem ? t('editSelection') : t('addSurplus')}
            </ZfTypography>
            <ZfStack direction="row" spacing="medium">
                <Controller
                    name="surplusType"
                    control={control}
                    rules={{ required: requiredField(t('selectFieldRequired')) }}
                    render={({ field, fieldState }) => (
                        <ZfFormField
                            label={t('Type')}
                            className="surplus-form-50-column"
                            mandatory={true}
                            alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                        >
                            <ZfInput
                                type="select"
                                value={field.value}
                                placeholder="Select"
                                error={fieldState.invalid}
                                onSelectChange={(e) => field.onChange(e.detail as SurplusType)}
                                onBlur={field.onBlur}
                            >
                                <ZfSelectOption value="INCOME" description={t('income')} />
                                <ZfSelectOption value="EXPENSE" description={t('expense')} />
                            </ZfInput>
                        </ZfFormField>
                    )}
                />
                <Controller
                    name="amount"
                    control={control}
                    rules={{ required: requiredField(t('amountFieldRequired')), validate: validateAmount }}
                    render={({ field, fieldState }) => (
                        <ZfFormField
                            label={t('wealthItemAmount')}
                            className="surplus-form-50-column"
                            mandatory={true}
                            alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                        >
                            <ZfInput
                                placeholder="Amount"
                                value={field.value}
                                type="number"
                                format={true}
                                error={fieldState.invalid}
                                onInputChange={(e) => field.onChange(e.detail)}
                                onBlur={field.onBlur}
                            >
                                <ZfTypography slot="leading" variant="label-medium">
                                    CHF
                                </ZfTypography>
                            </ZfInput>
                        </ZfFormField>
                    )}
                />
            </ZfStack>
            <ZfStack direction="row" spacing="medium">
                <Controller
                    name="fromDate"
                    control={control}
                    rules={{ required: requiredField(t('dateFieldRequired')) }}
                    render={({ field, fieldState }) => (
                        <ZfFormField
                            label={t('surplusFrom')}
                            mandatory={true}
                            className="w-100"
                            alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                        >
                            <ZfInput
                                type="date"
                                value={field.value}
                                error={fieldState.invalid}
                                onInputChange={(e) => onDateFieldChange(e.detail, field.onChange)}
                                onBlur={field.onBlur}
                            />
                        </ZfFormField>
                    )}
                />
                <Controller
                    name="toDate"
                    control={control}
                    rules={{ required: requiredField(t('dateFieldRequired')), validate: validateToDate }}
                    render={({ field, fieldState }) => (
                        <ZfFormField
                            label={t('surplusTo')}
                            mandatory={true}
                            className="w-100"
                            alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                        >
                            <ZfInput
                                type="date"
                                value={field.value}
                                error={fieldState.invalid}
                                onInputChange={(e) => onDateFieldChange(e.detail, field.onChange)}
                                onBlur={field.onBlur}
                            />
                        </ZfFormField>
                    )}
                />
            </ZfStack>
            {!isValidDateRange && !errors.fromDate && !errors.toDate && (
                <ZfTypography variant="label-small" color="danger">
                    {t('directSurplusDateRangeOverlapErrorMessage')}
                </ZfTypography>
            )}
            <ZfStack direction="row" spacing="medium" justify="space-between">
                {surplusItem ? (
                    <ZfTextButton size="medium" hierarchy="tertiary" kind="destructive" onClick={() => deleteItem(surplusItem?.id)}>
                        {t('modalDeleteButton')}
                    </ZfTextButton>
                ) : (
                    <ZfTextButton size="medium" hierarchy="tertiary" kind="standard" disabled={!isDirty} onClick={() => resetForm()}>
                        {t('reset')}
                    </ZfTextButton>
                )}
                <ZfStack direction="row" spacing="extra-small">
                    {surplusItem && (
                        <ZfTextButton size="medium" hierarchy="tertiary" onClick={cancelUpdate}>
                            {t('cancel')}
                        </ZfTextButton>
                    )}
                    <ZfTextButton size="medium" disabled={isSubmitDisabled()} onClick={onSubmit}>
                        {editMode === 'CREATE' ? t('add') : t('update')}
                    </ZfTextButton>
                </ZfStack>
            </ZfStack>
        </ZfStack>
    );
};
